// @flow

import * as React from 'react';

import capitalize from 'lodash/capitalize';

import type { DecoratedProduct } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Badge, Base } from '@gandi/ui';

import Counter from './Counter';
import LabelContent from './LabelContent';
import { PlanDisplay } from './PlanContent';
import type { Props as PlanRowProps } from './PlanRow';

import styles from './MailboxPlanRows.scss';

function getMailboxes(product: DecoratedProduct) {
  const { payload, id, productType } = product;

  if (productType === 'mailbox-standalone') {
    const configName = payload.config_name || product.prices[0].description;
    const capacity = configName === 'standard' ? '10GB' : '50GB';

    return {
      id,
      productType,
      name: configName || '',
      capacity,
      duration: payload.duration || payload.duration_month,
      quantity: payload.quantity || 1,
    };
  }

  return {
    id,
    productType,
    name: payload.name || '',
    capacity: payload.capacity,
    duration: payload.duration,
    quantity: product.quantity,
  };
}
type Props = {
  product?: DecoratedProduct,
  onChange?: (count: number) => mixed,
  isLoading?: boolean,
};

export function MailboxPlanRows({
  product,
  onChange,
  isLoading = false,
}: Props): React.MixedElement | null {
  const { __ } = useTranslator();

  if (!product) return null;

  const { id, name, capacity, duration, quantity } = getMailboxes(product);

  const inputId = `count-${id}`;
  const options: PlanRowProps['options'] =
    typeof onChange === 'function'
      ? [
          {
            id: inputId,
            content: (
              <LabelContent
                label={__('Number of mailboxes to create')}
                htmlFor={inputId}
              >
                <Counter
                  id={inputId}
                  count={quantity}
                  onChange={onChange}
                  isLoading={isLoading}
                />
              </LabelContent>
            ),
          },
        ]
      : [];

  if (product.identifier && options) {
    options.push({ id: product.identifier, content: product.identifier });
  }

  if (capacity && options)
    options.unshift({
      id: `capacity-${capacity}`,
      content: __('%(capacity)s storage', {
        capacity,
      }),
    });

  const currentDuration = duration
    ? duration >= 12
      ? __('Yearly billing')
      : __('Monthly billing')
    : undefined;

  return (
    <PlanDisplay
      sizeLabel={
        <Base flex gap="spacing-4x" flexAlignItemsCenter>
          {capitalize(name)}
          {currentDuration && <Badge>{currentDuration}</Badge>}
        </Base>
      }
      isSelected
      options={options}
      className={styles.plan}
    />
  );
}
